"use client";

import { useState } from "react";

import { BwbTab } from "./tabs/bwb-tab";
import { EnvironmentTab } from "./tabs/environment-tab";
import { ErrorTestingTab } from "./tabs/error-testing-tab";

/** To add a tab: write a component under `./tabs/` and append it here. */
const devPanelTabs = [
  { id: "bwb", label: "BWB", Component: BwbTab },
  { id: "environment", label: "Environment", Component: EnvironmentTab },
  { id: "errors", label: "Errors", Component: ErrorTestingTab },
];

/**
 * A floating dev/QA helper. A small button in the bottom-right corner opens a
 * panel with tabs. Not mounted in production — see the guard in `app/layout.tsx`.
 */
export function DevPanel() {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(devPanelTabs[0]?.id);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-16 right-4 z-[9999] rounded-full bg-neutral-900 px-3 py-2 font-mono text-xs text-white shadow-lg hover:bg-neutral-800"
      >
        Dev
      </button>
    );
  }

  const ActiveComponent =
    devPanelTabs.find((tab) => tab.id === activeTab)?.Component ??
    devPanelTabs[0]?.Component;

  return (
    <div className="fixed bottom-16 right-4 z-[9999] flex max-h-[70vh] w-[360px] flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white font-sans text-neutral-900 shadow-2xl">
      <div className="flex items-center justify-between bg-neutral-900 px-3 py-2 text-white">
        <span className="font-mono text-xs font-semibold">
          Dev Panel
          <span className="ml-2 rounded bg-white/15 px-1.5 py-0.5 text-[10px] uppercase">
            {process.env.NEXT_PUBLIC_ENV ?? "local"}
          </span>
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close dev panel"
          className="rounded px-1.5 text-lg leading-none text-white/70 hover:text-white"
        >
          ×
        </button>
      </div>

      {devPanelTabs.length > 1 && (
        <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-neutral-200 bg-neutral-50 px-2 py-1.5">
          {devPanelTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={
                "whitespace-nowrap rounded px-2 py-1 text-xs " +
                (tab.id === activeTab
                  ? "bg-neutral-900 text-white"
                  : "text-neutral-600 hover:bg-neutral-200")
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="grow overflow-y-auto p-3">
        {ActiveComponent ? (
          <ActiveComponent />
        ) : (
          <p className="text-xs text-neutral-500">No tabs registered.</p>
        )}
      </div>
    </div>
  );
}
